import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function IncomeLinkModal({ row, onClose, onLinked, apiPath }) {
  const [isRecurring, setIsRecurring] = useState(row?.linked_income_stream_id ? true : false);
  const [streams, setStreams] = useState([]);
  const [selectedStreamId, setSelectedStreamId] = useState(row?.linked_income_stream_id || '');
  const [loading, setLoading] = useState(false);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // New series fields
  const [isNewSeries, setIsNewSeries] = useState(false);
  const [newSourceName, setNewSourceName] = useState(row?.description || '');
  const [frequency, setFrequency] = useState('bi_weekly');
  const [recurringType, setRecurringType] = useState('fixed');
  const [isProjectable, setIsProjectable] = useState(true);

  useEffect(() => {
    fetchStreams();
  }, []);

  useEffect(() => {
    if (row?.linked_income_stream_id) {
        setIsRecurring(true);
        setSelectedStreamId(row.linked_income_stream_id);
    } else {
        setIsRecurring(false);
        setSelectedStreamId('');
    }
  }, [row]);

  const fetchStreams = async () => {
    setStreamsLoading(true);
    try {
      const res = await axios.get(`/api/balance_sheet/assets/cash_accounts/transactions/income/streams/`);
      const data = res.data;
      setStreams(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch income streams", e);
    } finally {
      setStreamsLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      let streamId = selectedStreamId;
      
      if (isRecurring && isNewSeries) {
        // Create new stream first
        const createRes = await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/income/streams/`, {
          source: newSourceName,
          display_name: newSourceName,
          recurring_frequency: frequency,
          is_projectable: isProjectable,
          recurring_type: recurringType
        });
        const newStream = createRes.data;
        streamId = newStream.id;
      }

      const payload = {
        kind: isRecurring ? 'recurring' : 'non_recurring',
        source: isRecurring ? (streams.find(s => String(s.id) === String(streamId))?.source || newSourceName) : (row.description || 'Income'),
        description: row.description,
        stream_id: isRecurring ? streamId : null,
        refresh_projections: true
      };

      const finalApiPath = apiPath || `/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/record-income/`;

      await axios.post(finalApiPath, payload);

      onLinked();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to record income");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000 }}>
      <div style={{ width:'500px', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb', padding:20 }}>
        <div style={{ fontSize:18, fontWeight:700, marginBottom:16, borderBottom:'1px solid #374151', paddingBottom:10 }}>
          {row?.expense_category === 'tax_refund' ? 'Categorize Tax Refund' : 'Categorize as Income'}
        </div>
        
        {error && <div className="alert alert-danger py-2">{error}</div>}

        <div className="mb-3">
          <label className="form-check form-switch">
            <input className="form-check-input" type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} />
            <span className="form-check-label">Recurring Income?</span>
          </label>
        </div>

        {isRecurring && (
          <div className="card bg-dark border-secondary p-3 mb-3">
            {!isNewSeries ? (
              <>
                <label className="form-label">Existing Income Series</label>
                <select 
                  className="form-select mb-2" 
                  value={selectedStreamId} 
                  onChange={e => setSelectedStreamId(e.target.value)}
                  disabled={streamsLoading}
                >
                  <option value="">Select series...</option>
                  {streams.map(s => (
                    <option key={s.id} value={s.id}>{s.display_name || s.source} ({s.recurring_frequency})</option>
                  ))}
                </select>
                <button className="btn btn-sm btn-outline-info" onClick={() => setIsNewSeries(true)}>
                  + Create New Series
                </button>
              </>
            ) : (
              <>
                <div className="mb-2">
                  <label className="form-label">Series Name (Employer / Source)</label>
                  <input 
                    className="form-control" 
                    value={newSourceName} 
                    onChange={e => setNewSourceName(e.target.value)}
                    placeholder="e.g. Acme Corp Paycheck"
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Frequency</label>
                  <select className="form-select" value={frequency} onChange={e => setFrequency(e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="bi_weekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Type (Fixed vs. Variable)</label>
                  <select className="form-select" value={recurringType} onChange={e => setRecurringType(e.target.value)}>
                    <option value="fixed">Fixed</option>
                    <option value="variable">Variable</option>
                  </select>
                </div>
                <div className="form-check mb-2">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    checked={isProjectable} 
                    onChange={e => setIsProjectable(e.target.checked)} 
                    id="isProj"
                  />
                  <label className="form-check-label" htmlFor="isProj">Enable Projections</label>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsNewSeries(false)}>
                  Back to existing
                </button>
              </>
            )}
          </div>
        )}

        {!isRecurring && (
          <div className="text-muted mb-3">
            This will be tagged as a one-time income event.
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button 
            className="btn btn-primary" 
            onClick={handleSave} 
            disabled={loading || (isRecurring && !isNewSeries && !selectedStreamId) || (isRecurring && isNewSeries && !newSourceName)}
          >
            {loading ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
